import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {BehaviorSubject, Subscription} from 'rxjs';
import {delay} from 'rxjs/operators';

// Models
import {ItemVariation} from '../shared/models/item-variation.model';

// Services
import {ItemVariationService} from '../shared/services/item-variation.service';
import {TranslateService} from '@ngx-translate/core';

// SE FE components
import {SeFeApiService} from 'se-fe-api';
import {SeFeModalComponent} from 'se-fe-modal';
import {SeFePaginationChange, SeFePaginationOptions} from 'se-fe-pagination';
import {SeFeTableComponent, SeFeTableDataSource} from 'se-fe-table';
import {SeFeToolbarComponent} from 'se-fe-toolbar';
import { SeFeThemesLoader } from 'se-fe-themes';

@Component({
  selector: 'sale-item-selector-element',
  templateUrl: './sale-item-selector.component.html',
  styleUrls: ['./sale-item-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SaleItemSelectorComponent implements OnInit, OnDestroy {
  public amountSelected: number;
  public displayTableColumns = new BehaviorSubject<string[]>(['name', 'sku', 'price', 'remove']);
  public displayTableDataSource: SeFeTableDataSource;
  public isLoaded = false;
  public itemVariationList: ItemVariation[] = [];
  public paginationOptions: SeFePaginationOptions;
  public searchTerm = '';
  public selectedItems: ItemVariation[];
  public selectedItemsLoaded = true;
  public selectForm: FormGroup;
  public selectTableColumns = new BehaviorSubject<string[]>(['checkbox', 'name', 'sku', 'price']);
  public selectTableDataSource: SeFeTableDataSource;
  private formSubscription: Subscription;

  @Input() organizationId: string;
  @Input() modalHeader: string;
  @Input() selectedItemIds: string;
  @Output() public selectionSaved = new EventEmitter<any>();

  @ViewChild('modal') public modal: SeFeModalComponent;
  @ViewChild(SeFeTableComponent) public displayTable!: SeFeTableComponent<ElementRef>;
  @ViewChild('displayTable') set displayTableComponent(displayTable: SeFeTableComponent<ElementRef>) {
    if (!displayTable) { return; }
    this.displayTable = displayTable;
    this.displayTable.dataSource = this.displayTableDataSource;
  }
  @ViewChild(SeFeTableComponent) public selectTable!: SeFeTableComponent<ElementRef>;
  @ViewChild('selectTable') set selectTableComponent(selectTable: SeFeTableComponent<ElementRef>) {
    if (!selectTable) { return; }
    this.selectTable = selectTable;
    this.selectTable.dataSource = this.selectTableDataSource;
  }
  @ViewChild(SeFeToolbarComponent) public toolbar: SeFeToolbarComponent;

  constructor(
    private apiService: SeFeApiService,
    private itemVariationService: ItemVariationService,
    private formBuilder: FormBuilder,
    private translate: TranslateService
  ) {
    translate.setDefaultLang('en');
    translate.use('en');
    SeFeThemesLoader.load({
      supportedThemes: 'light',
      url: 'https://se-fe-angular-services.ui.sportngin.com'
    });
  }

  public async ngOnInit(): Promise<void> {
    this.paginationOptions = { label: 'sale items', name: 'itemVariation' };
    this.amountSelected = 0;
    this.selectTableDataSource = new SeFeTableDataSource(undefined);
    this.displayTableDataSource = new SeFeTableDataSource(undefined);
    this.selectedItems = [];
    await this.loadItems();
    await this.resetModal();
  }

  public ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
  }

  public async handlePagination(paginationData: SeFePaginationChange): Promise<void> {
    this.paginationOptions.limit = paginationData.limit;
    this.paginationOptions.currentPage = paginationData.currentPage;
    await this.loadItems();
  }

  public async search(searchTerm: string): Promise<void> {
    this.searchTerm = searchTerm;
    this.paginationOptions.currentPage = 1;
    await this.loadItems();
  }

  public async saveSelection(): Promise<void> {
    this.modal.close();
    this.updateDisplayTable();
    this.selectionSaved.emit(this.currentlySelected());
    await this.resetModal();
  }

  public async resetSelection(): Promise<void> {
    this.modal.close();
    await this.resetModal();
  }

  public removeItem(itemId: number): void {
    const itemIndex = this.selectedItemIndex(itemId);
    if (itemIndex !== -1) {
      this.selectedItems.splice(itemIndex, 1);
      this.updateDisplayTable();
      this.updateForm();
      this.selectionSaved.emit(this.currentlySelected());
    }
  }

  public clickRow(e: Event): void {
    const row = (e.target as HTMLElement).closest('tr');
    const clickedElement = (e.target as HTMLElement).closest('a, button, input, [tabindex] [role="button"]');
    if (clickedElement && row.contains(clickedElement)) {
      return;
    }
    const checkboxElement = row.querySelector('input[type=checkbox]') as HTMLElement;
    checkboxElement.click();
  }

  // Private methods
  private async resetModal(): Promise<void> {
    this.paginationOptions.currentPage = 1;
    this.paginationOptions.limit = 25;
    this.searchTerm = '';
    this.toolbar?.searchForm.setValue({'searchTerm': ''});
    if (this.selectedItemIds?.length) {
      await this.loadSelectedItems(this.selectedItemIds);
    }
    // await this.loadItems();
  }

  private async loadItems(): Promise<void> {
    this.isLoaded = false;
    const params = {
      ...this.baseParams,
      ...this.paginationParams,
      ...this.searchParams,
      ...this.sortParams
    };
    const results = await this.apiService.get('finance/item_variations', { params }).toPromise()
      .finally(() => { this.isLoaded = true; });
    this.itemVariationList = [...results.result as ItemVariation[]];
    this.selectTableDataSource.data = this.itemVariationList;
    this.setPaginationOptions(results.metadata.pagination);
    this.updateForm();
  }

  private async loadSelectedItems(ids: string): Promise<void> {
    this.selectedItemsLoaded = false;
    const params = {
      id_list: this.selectedItemIds,
      unpaginated: true,
      ...this.baseParams
    };
    await this.itemVariationService.findAll(params)
      .then((results) => {
        this.selectedItems = results;
        this.updateDisplayTable();
        this.selectedItemsLoaded = true;
      });
  }

  private updateDisplayTable(): void {
    this.sortSelected();
    this.displayTableDataSource.data = this.selectedItems;
    this.selectedItemIds = this.currentlySelected().join(',');
    this.amountSelected = this.selectedItems.length;
  }

  private updateForm(): void {
    this.selectForm = this.formBuilder.group({});
    this.formSubscription?.unsubscribe();
    this.itemVariationList.forEach(item => {
      const isChecked = this.currentlySelected().includes(item.id);
      this.selectForm.addControl(String(item.id), new FormControl(isChecked));
    });
    this.formSubscription = this.selectForm.valueChanges.subscribe(formValue => this.toggleSelection(formValue));
  }

  private selectedItemIndex(itemId: number) {
    return this.selectedItems.findIndex((item) => item.id === itemId);
  }

  private currentlySelected() {
    return this.selectedItems.map((item) => item.id);
  }

  private toggleSelection(formValue: { [id: string]: boolean; }) {
    for (const id in formValue) {
      if (!id) { continue; }
      const itemIndex = this.selectedItemIndex(parseInt(id, 10));
      if (!!formValue[id] && itemIndex === -1) {
        const itemModel = this.itemVariationList.find((item) => item.id.toString() === id);
        this.selectedItems.push(itemModel);
      }
      if (!formValue[id] && itemIndex !== -1) {
        this.selectedItems.splice(itemIndex, 1);
      }
    }
  }

  private setPaginationOptions(paginationData): void {
    this.paginationOptions.name = 'itemListPagination';
    this.paginationOptions.label = 'sale items';
    this.paginationOptions.currentPage = paginationData.current_page;
    this.paginationOptions.total = paginationData.total;
  }

  private sortSelected(): void {
    this.selectedItems.sort((a, b) => a.full_name.localeCompare(b.full_name));
  }

  // Build Query Params
  private get baseParams(): any {
    return {
      organization_id: this.organizationId,
      include_zero_rate: 1
    };
  }

  private get paginationParams(): any {
    return {
      page: this.paginationOptions.currentPage || 1,
      per_page: this.paginationOptions.limit || 25,
    };
  }

  private get searchParams(): any {
    return {
      search_fields: 'full_name,sku',
      search_term: this.searchTerm
    };
  }

  private get sortParams(): any {
    return {
      'order[full_name]': 'asc'
    };
  }
}
